/**
 * HypeClipComposer — multi-format canvas clip generator.
 * Produces share-ready PNGs in vertical / landscape / square / story
 * presets without any server-side rendering. Reuses the same primitives
 * as clipExport but accepts a `format` and a `template` kind.
 */
export type ClipFormat = "vertical" | "landscape" | "square" | "story";
export type ClipTemplate =
  | "nation_hype"
  | "goal_pressure"
  | "cinematic_moment"
  | "world_status"
  | "community_explosion";

export interface HypeClipInput {
  template: ClipTemplate;
  format: ClipFormat;
  headline: string;
  subline?: string;
  emoji?: string;
  accentHex?: string;
  brand?: string;
}

const SIZES: Record<ClipFormat, { w: number; h: number; label: string }> = {
  vertical: { w: 1080, h: 1920, label: "TikTok / Reels / Shorts" },
  landscape: { w: 1920, h: 1080, label: "X / YouTube / Twitch" },
  square: { w: 1080, h: 1080, label: "Instagram / WhatsApp" },
  story: { w: 1080, h: 1920, label: "WhatsApp / Instagram Story" },
};

const TEMPLATE_DEFAULTS: Record<ClipTemplate, { emoji: string; accent: string }> = {
  nation_hype: { emoji: "🔥", accent: "#E11D48" },
  goal_pressure: { emoji: "⚽", accent: "#FF6A00" },
  cinematic_moment: { emoji: "🎬", accent: "#7C3AED" },
  world_status: { emoji: "🌍", accent: "#2DD4A8" },
  community_explosion: { emoji: "💥", accent: "#F59E0B" },
};

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

export function listClipFormats() { return Object.entries(SIZES).map(([id, v]) => ({ id: id as ClipFormat, ...v })); }
export function listClipTemplates(): ClipTemplate[] {
  return ["nation_hype", "goal_pressure", "cinematic_moment", "world_status", "community_explosion"];
}

export async function buildHypeClip(input: HypeClipInput): Promise<string> {
  const { w: W, h: H } = SIZES[input.format];
  const defaults = TEMPLATE_DEFAULTS[input.template];
  const accent = input.accentHex ?? defaults.accent;
  const emoji = input.emoji ?? defaults.emoji;
  const { r, g, b } = hexToRgb(accent);

  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no-canvas");

  // background
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#0a0a1a");
  grad.addColorStop(1, "#1a0a14");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // accent glow
  const cx = W / 2;
  const cy = H * (input.format === "landscape" ? 0.45 : 0.32);
  const glow = ctx.createRadialGradient(cx, cy, 40, cx, cy, Math.max(W, H) * 0.5);
  glow.addColorStop(0, `rgba(${r},${g},${b},0.55)`);
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // emoji
  ctx.textAlign = "center";
  const emojiSize = input.format === "landscape" ? 200 : 240;
  ctx.font = `${emojiSize}px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'`;
  ctx.fillText(emoji, cx, cy + emojiSize * 0.35);

  // headline
  ctx.fillStyle = "#ffffff";
  const headSize = input.format === "landscape" ? 84 : 92;
  ctx.font = `800 ${headSize}px system-ui, -apple-system, Segoe UI, Roboto`;
  const hLines = wrap(ctx, input.headline, W - 160);
  const headStartY = cy + emojiSize * 0.9;
  hLines.slice(0, 3).forEach((ln, i) => ctx.fillText(ln, cx, headStartY + i * (headSize + 18)));

  // subline
  if (input.subline) {
    ctx.fillStyle = "rgba(255,255,255,0.78)";
    const subSize = input.format === "landscape" ? 40 : 44;
    ctx.font = `500 ${subSize}px system-ui, -apple-system, Segoe UI, Roboto`;
    const sLines = wrap(ctx, input.subline, W - 200);
    const subStartY = headStartY + hLines.length * (headSize + 18) + 30;
    sLines.slice(0, 3).forEach((ln, i) => ctx.fillText(ln, cx, subStartY + i * (subSize + 14)));
  }

  // brand
  ctx.fillStyle = `rgba(${r},${g},${b},1)`;
  ctx.font = `900 ${input.format === "landscape" ? 44 : 56}px system-ui`;
  ctx.fillText(input.brand ?? "VerseSport", cx, H - 120);
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = `500 ${input.format === "landscape" ? 26 : 32}px system-ui`;
  ctx.fillText("versesport.com", cx, H - 70);

  return canvas.toDataURL("image/png");
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function exportHypeClip(input: HypeClipInput) {
  const dataUrl = await buildHypeClip(input);
  const file = `versesport-${input.template}-${input.format}.png`;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const f = new File([blob], file, { type: "image/png" });
    const nav = navigator as Navigator & {
      canShare?: (d: { files?: File[] }) => boolean;
      share?: (d: ShareData & { files?: File[] }) => Promise<void>;
    };
    if (nav.canShare?.({ files: [f] }) && nav.share) {
      await nav.share({ files: [f], title: "VerseSport", text: input.headline });
      return dataUrl;
    }
  } catch { /* fall through */ }
  downloadDataUrl(dataUrl, file);
  return dataUrl;
}